/**
 * Rithmic's own account-risk figures (ADR-0045): drawdown room and daily-loss room derived only
 * from Rithmic values (356 threshold, 305 loss limit, 451 money), with the formula and every input
 * shown. Pure; no protobuf runtime.
 *
 * - Every figure carries an explicit source (docs/RITHMIC-ACCOUNT-DATA.md "How HELM applies
 *   this"): `rithmic` (computed from Rithmic inputs only; first choice), `helm_calculated` (the
 *   second choice, CL-04: computed by HELM from owner-entered firm rules and HELM's stored daily
 *   history — never labelled Rithmic, never verified by an R|Trader match), `owner_entry` (a raw
 *   owner-entered firm-rule value such as the profit target, CL-04) or `unavailable` (with the
 *   reason). `chooseRoom` applies that order; the second source is computed in
 *   tables/firmFigures.ts (ADR-0047) and never mixes its inputs into a Rithmic figure.
 * - A Rithmic figure is "unverified" until the owner records a R|Trader Pro match for that account
 *   and formula (`broker_verification/{accountId}`, docs/RITHMIC-RTRADER-MATCH.md). Never
 *   automatic. A match made while flat covers flat only; a changed auto-liquidate criterion voids it.
 * - Any missing, stale or unsupported input makes the figure unavailable. There is no fallback
 *   to HELM's equity, presets, overrides, account-name detection or the legacy risk engine.
 */

/** `helm.v1.BrokerRmsUpdates` (checked against the generated enum in the tests). */
export const RMS_UPDATES = {
  UNSPECIFIED: 0,
  OFF: 1,
  PENDING: 2,
  ACCEPTED: 3,
  REFUSED: 4,
  NO_ANSWER: 5,
} as const;
/** `helm.v1.BrokerLossLimit`. */
export const LOSS_LIMIT = { UNSPECIFIED: 0, ABSENT: 1, NO_LIMIT: 2, ZERO: 3, INVALID: 4, VALUE: 5 } as const;
/** `helm.v1.BrokerRiskSource`. */
export const RISK_SOURCE = { UNSPECIFIED: 0, RMS_UPDATE: 1, ACCOUNT_LIST: 2 } as const;

/** The `AccountUpdate.broker_risk` fields this module reads (proto field names, camelCase). */
export type BrokerRiskWire = {
  rmsUpdates: number;
  rmsUpdatesDetail: string;
  autoLiqThresholdCents?: bigint | undefined;
  autoLiqThresholdRaw: string;
  autoLiqThresholdSource: number;
  autoLiqThresholdReceivedNs: bigint;
  autoLiqPeakBalanceCents?: bigint | undefined;
  autoLiqPeakBalanceRaw: string;
  autoLiqPeakBalanceBrokerNs: bigint;
  rmsInfoCurrent: boolean;
  rmsInfoReceivedNs: bigint;
  lossLimitState: number;
  lossLimitCents?: bigint | undefined;
  autoLiquidateCriteria: string;
  autoLiquidate: number;
  rmsAutoLiquidateThresholdCents?: bigint | undefined;
  rmsMinAccountBalanceCents?: bigint | undefined;
};

export type FigureSource = "rithmic" | "helm_calculated" | "owner_entry" | "unavailable";

/** Label of a room figure HELM computed from the owner's firm rules (CL-04). */
export const HELM_CALCULATED_LABEL = "TradePrizm-calculated from your firm rules";
export type Verification = "unverified" | "verified";
export type FigureKind = "trailing_drawdown" | "daily_loss";

export type FormulaDef = {
  id: string;
  /** Short, user-facing. */
  label: string;
  /** Exactly which Rithmic fields, and what HELM does with them. */
  expression: string;
  /** Shown while no verification exists. */
  isDefault: boolean;
};

/**
 * Trailing drawdown room. Default: Rithmic's own definition in R|Trader Pro's
 * auto_liquidate_criteria.htm — "Trailing Minimum Account Balance is Account Balance − Liquidate
 * Threshold … If the account balance falls below the trailing minimum account balance, the account
 * is liquidated" — read with 451 `account_balance` as that Account Balance and 356
 * `auto_liq_threshold_current_value` as the current trailing minimum. Adding open P&L would be a
 * HELM reconstruction; whether R|Trader's figure includes it is what the match settles (they agree
 * while flat).
 */
export const TRAILING_DD_FORMULAS: readonly FormulaDef[] = [
  {
    id: "balance_minus_threshold",
    label: "Account balance − auto-liq threshold",
    expression:
      "Rithmic 451 account_balance − Rithmic 356 auto_liq_threshold_current_value (TradePrizm subtracts)",
    isDefault: true,
  },
  {
    id: "balance_plus_open_minus_threshold",
    label: "(Account balance + open P&L) − auto-liq threshold",
    expression:
      "(Rithmic 451 account_balance + Rithmic 451 open_position_pnl) − Rithmic 356 auto_liq_threshold_current_value (TradePrizm adds and subtracts)",
    isDefault: false,
  },
];

/**
 * Daily loss room. Default: 305 `loss_limit` (a positive amount) plus 451 `day_pnl` (signed,
 * negative = loss), so a loss uses up the limit and a profit adds room. The realized-only variant
 * is kept for the match.
 */
export const DAILY_LOSS_FORMULAS: readonly FormulaDef[] = [
  {
    id: "loss_limit_plus_day_pnl",
    label: "Loss limit + day P&L",
    expression:
      "Rithmic 305 loss_limit + Rithmic 451 day_pnl (TradePrizm adds; day_pnl is negative for a loss)",
    isDefault: true,
  },
  {
    id: "loss_limit_plus_day_closed_pnl",
    label: "Loss limit + day closed P&L",
    expression: "Rithmic 305 loss_limit + Rithmic 451 day_closed_pnl (TradePrizm adds; realized only)",
    isDefault: false,
  },
];

export const FORMULAS: Readonly<Record<FigureKind, readonly FormulaDef[]>> = {
  trailing_drawdown: TRAILING_DD_FORMULAS,
  daily_loss: DAILY_LOSS_FORMULAS,
};

export type FigureInput = {
  /** "Rithmic account balance (451 account_balance)". */
  label: string;
  valueCents: bigint | null;
  /** Exact Rithmic string where the wire has one. */
  raw: string | null;
  /** Unavailable reason for this input, "" when present. */
  reason: string;
};

export type CandidateValue = { formula: FormulaDef; valueCents: bigint | null; reason: string };

export type BrokerFigure = {
  kind: FigureKind;
  source: FigureSource;
  /** Non-null only when `source !== "unavailable"`. */
  valueCents: bigint | null;
  /** Rithmic figures only. */
  verification: Verification | null;
  /** "Rithmic · unverified" | "Rithmic · verified" | "Unavailable". */
  label: string;
  /** Why unavailable, or why still unverified. */
  reason: string;
  formula: FormulaDef | null;
  candidates: CandidateValue[];
  inputs: FigureInput[];
  /**
   * The other source's figure for the same room (ADR-0047), kept whole for the details: the
   * HELM-calculated one beside a Rithmic figure, or the unavailable Rithmic one beside a
   * HELM-calculated figure. Never merged into this figure's value or inputs.
   */
  alternate?: BrokerFigure | null;
  /** Shown with the figure: "Differs from Rithmic by $X …", or why the second source is used. */
  note?: string;
};

/** Rithmic and HELM-calculated figures further apart than this get the "differs" note. */
export const DIFFERS_TOLERANCE_CENTS = 100n;

// ---------------------------------------------------------------------------------------------
// Owner-confirmed verification record: synced document `broker_verification/{accountId}`.

export type VerificationEntry = {
  formula: string;
  /** ISO 8601. */
  confirmed_at: string;
  device_id: string;
  device_label: string;
  /** "flat": open P&L was 0 at the match, so it covers flat accounts only. */
  scope: "flat" | "open_position";
  /** Rithmic 305 auto_liquidate_criteria at the match; a different current criterion voids it. */
  criteria: string;
  /** HELM's figure at the match (cents, decimal string). */
  helm_value_cents: string;
  /** What the owner read in R|Trader Pro, as typed. */
  rtrader_value: string;
  note: string;
};

export type BrokerVerificationDoc = {
  schema: 1;
  account_id: string;
  trailing_drawdown: VerificationEntry | null;
  daily_loss: VerificationEntry | null;
};

export const BROKER_VERIFICATION_DOC = "broker_verification" as const;

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);

function parseEntry(kind: FigureKind, raw: unknown): VerificationEntry | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const formula = str(r["formula"]);
  const confirmedAt = str(r["confirmed_at"]);
  const scope = r["scope"];
  if (formula === null || !FORMULAS[kind].some((f) => f.id === formula)) return null;
  if (confirmedAt === null || Number.isNaN(Date.parse(confirmedAt))) return null;
  if (scope !== "flat" && scope !== "open_position") return null;
  return {
    formula,
    confirmed_at: confirmedAt,
    device_id: str(r["device_id"]) ?? "",
    device_label: str(r["device_label"]) ?? "",
    scope,
    criteria: str(r["criteria"]) ?? "",
    helm_value_cents: str(r["helm_value_cents"]) ?? "",
    rtrader_value: str(r["rtrader_value"]) ?? "",
    note: str(r["note"]) ?? "",
  };
}

/** A stored record, validated; anything malformed counts as no verification. */
export function parseVerification(accountId: string, raw: unknown): BrokerVerificationDoc | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (r["schema"] !== 1 || r["account_id"] !== accountId) return null;
  return {
    schema: 1,
    account_id: accountId,
    trailing_drawdown: parseEntry("trailing_drawdown", r["trailing_drawdown"]),
    daily_loss: parseEntry("daily_loss", r["daily_loss"]),
  };
}

/** The record after the owner confirms (or, with `entry` null, withdraws) one figure's match. */
export function withVerification(
  accountId: string,
  current: BrokerVerificationDoc | null,
  kind: FigureKind,
  entry: VerificationEntry | null,
): BrokerVerificationDoc {
  const base: BrokerVerificationDoc = current ?? {
    schema: 1,
    account_id: accountId,
    trailing_drawdown: null,
    daily_loss: null,
  };
  return { ...base, [kind]: entry };
}

// ---------------------------------------------------------------------------------------------
// Derivation

export type BrokerRiskInputs = {
  /** Gateway connected, replay caught up, positions confirmed (account-finance gates). */
  connected: boolean;
  replayReady: boolean;
  /** Exact 303 denomination; only "USD" has a verified cent display. */
  brokerCurrency: string | null;
  /** `accountFinanceReason` of the 451 money ("" = current). */
  financeReason: string;
  balanceCents: bigint | null;
  unrealizedCents: bigint | null;
  dayPnlCents: bigint | null;
  realizedCents: bigint | null;
  risk: BrokerRiskWire | null;
  verification: BrokerVerificationDoc | null;
};

/** Why order-plant Rithmic values cannot be shown at all ("" = they can). */
export function brokerRiskReason(i: BrokerRiskInputs): string {
  if (!i.connected) return "Gateway disconnected";
  if (!i.replayReady) return "Waiting for gateway replay to finish";
  if (i.risk === null) return "No current Rithmic order-plant coverage for this account";
  if (i.brokerCurrency === null) return "Rithmic account currency unavailable";
  if (i.brokerCurrency !== "USD")
    return "Rithmic currency denomination is not supported by the verified cent display";
  return "";
}

/** "Unavailable: <reason>", without doubling a reason that already says so. */
export function unavailableText(reason: string): string {
  return reason.startsWith("Unavailable") ? reason : `Unavailable: ${reason}`;
}

/** The 356 threshold's availability reason ("" = present and current). */
export function thresholdReason(i: BrokerRiskInputs): string {
  const base = brokerRiskReason(i);
  if (base !== "") return base;
  const r = i.risk as BrokerRiskWire;
  switch (r.rmsUpdates) {
    case RMS_UPDATES.ACCEPTED:
      break;
    case RMS_UPDATES.REFUSED: {
      // The gateway's detail is "Rithmic refused RMS updates: <rp_code words>".
      const words = r.rmsUpdatesDetail.replace(/^Rithmic refused RMS updates:?\s*/, "");
      return `Unavailable (Rithmic refused RMS updates)${words ? `: ${words}` : ""}`;
    }
    case RMS_UPDATES.OFF:
      return r.rmsUpdatesDetail || "Rithmic RMS updates are off on this gateway";
    case RMS_UPDATES.NO_ANSWER:
      return r.rmsUpdatesDetail || "Rithmic did not answer the RMS update subscription";
    default:
      return r.rmsUpdatesDetail || "Waiting for Rithmic RMS updates";
  }
  if (r.autoLiqThresholdCents === undefined) {
    return r.autoLiqThresholdRaw !== ""
      ? `Rithmic sent a threshold that is not an amount ("${r.autoLiqThresholdRaw}")`
      : "Rithmic has not reported the current auto-liquidation threshold";
  }
  return "";
}

/** The 305 loss limit's availability reason ("" = a usable amount). */
export function lossLimitReason(i: BrokerRiskInputs): string {
  const base = brokerRiskReason(i);
  if (base !== "") return base;
  const r = i.risk as BrokerRiskWire;
  if (!r.rmsInfoCurrent) return "Rithmic RMS info (305) not received on this connection";
  switch (r.lossLimitState) {
    case LOSS_LIMIT.VALUE:
      return r.lossLimitCents === undefined ? "Rithmic loss limit missing" : "";
    case LOSS_LIMIT.ABSENT:
      return "Rithmic reports no loss limit for this account (field absent)";
    case LOSS_LIMIT.NO_LIMIT:
      return "Rithmic loss limit disabled (Rithmic's 9,999,999 no-limit placeholder)";
    case LOSS_LIMIT.ZERO:
      return "Rithmic reported a loss limit of 0; its meaning is unverified, so it is not used";
    case LOSS_LIMIT.INVALID:
      return "Rithmic sent a loss limit that is not a valid amount";
    default:
      return "Rithmic loss limit not received";
  }
}

const moneyReason = (i: BrokerRiskInputs, value: bigint | null, name: string): string =>
  i.financeReason !== ""
    ? `${name} unavailable: ${i.financeReason}`
    : value === null
      ? `${name} not reported by Rithmic`
      : "";

function inputsOf(kind: FigureKind, i: BrokerRiskInputs): FigureInput[] {
  const r = i.risk;
  const money = (label: string, value: bigint | null): FigureInput => {
    const reason = moneyReason(i, value, label);
    return { label, valueCents: reason === "" ? value : null, raw: null, reason };
  };
  if (kind === "trailing_drawdown") {
    const reason = thresholdReason(i);
    return [
      money("Rithmic account balance (451 account_balance)", i.balanceCents),
      money("Rithmic open P&L (451 open_position_pnl)", i.unrealizedCents),
      {
        label: "Rithmic auto-liq threshold (356 auto_liq_threshold_current_value)",
        valueCents: reason === "" ? (r?.autoLiqThresholdCents ?? null) : null,
        raw: r && r.autoLiqThresholdRaw !== "" ? r.autoLiqThresholdRaw : null,
        reason,
      },
    ];
  }
  const reason = lossLimitReason(i);
  return [
    {
      label: "Rithmic loss limit (305 loss_limit)",
      valueCents: reason === "" ? (r?.lossLimitCents ?? null) : null,
      raw: null,
      reason,
    },
    money("Rithmic day P&L (451 day_pnl)", i.dayPnlCents),
    money("Rithmic day closed P&L (451 day_closed_pnl)", i.realizedCents),
  ];
}

/**
 * Each formula as signed terms over `inputsOf` (by index): trailing [balance, open P&L,
 * threshold], daily loss [loss limit, day P&L, day closed P&L].
 */
const TERMS: Readonly<Record<string, readonly (readonly [number, bigint])[]>> = {
  balance_minus_threshold: [
    [0, 1n],
    [2, -1n],
  ],
  balance_plus_open_minus_threshold: [
    [0, 1n],
    [1, 1n],
    [2, -1n],
  ],
  loss_limit_plus_day_pnl: [
    [0, 1n],
    [1, 1n],
  ],
  loss_limit_plus_day_closed_pnl: [
    [0, 1n],
    [2, 1n],
  ],
};

function candidate(f: FormulaDef, inputs: readonly FigureInput[]): CandidateValue {
  let valueCents = 0n;
  for (const [index, sign] of TERMS[f.id] ?? []) {
    const input = inputs[index];
    if (input === undefined) return { formula: f, valueCents: null, reason: "Unknown input" };
    if (input.valueCents === null) {
      return { formula: f, valueCents: null, reason: input.reason || `${input.label} unavailable` };
    }
    valueCents += sign * input.valueCents;
  }
  return { formula: f, valueCents, reason: "" };
}

/** Whether `entry` still verifies the figure now, else why not. */
function verificationGap(entry: VerificationEntry, i: BrokerRiskInputs): string {
  const criteria = i.risk?.autoLiquidateCriteria ?? "";
  if (entry.criteria !== criteria) {
    return "Rithmic's auto-liquidate criteria changed since the R|Trader Pro match";
  }
  if (entry.scope === "flat" && i.unrealizedCents !== 0n) {
    return "The R|Trader Pro match was made while flat; it does not cover open P&L";
  }
  return "";
}

const UNVERIFIED_REASON =
  "Not yet matched against R|Trader Pro for this account (docs/RITHMIC-RTRADER-MATCH.md).";

/** One Rithmic-derived figure for one account. */
export function brokerFigure(kind: FigureKind, i: BrokerRiskInputs): BrokerFigure {
  const inputs = inputsOf(kind, i);
  const candidates = FORMULAS[kind].map((f) => candidate(f, inputs));
  const entry = i.verification?.[kind] ?? null;
  const gap = entry === null ? UNVERIFIED_REASON : verificationGap(entry, i);
  const verified = entry !== null && gap === "";
  const chosen = verified
    ? (candidates.find((c) => c.formula.id === entry.formula) as CandidateValue)
    : (candidates.find((c) => c.formula.isDefault) as CandidateValue);
  if (chosen.valueCents === null) {
    return {
      kind,
      source: "unavailable",
      valueCents: null,
      verification: null,
      label: "Unavailable",
      reason: chosen.reason,
      formula: chosen.formula,
      candidates,
      inputs,
    };
  }
  return {
    kind,
    source: "rithmic",
    valueCents: chosen.valueCents,
    verification: verified ? "verified" : "unverified",
    label: verified ? "Rithmic · verified" : "Rithmic · unverified",
    reason: verified ? "" : gap,
    formula: chosen.formula,
    candidates,
    inputs,
  };
}

const absCents = (c: bigint): bigint => (c < 0n ? -c : c);
const dollarsText = (c: bigint): string => {
  const a = absCents(c);
  return `$${(a / 100n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${(a % 100n).toString().padStart(2, "0")}`;
};

/**
 * One room figure from its sources in the owner's order: the Rithmic figure when it has a value;
 * otherwise a HELM-calculated figure (ADR-0047) when one is supplied and has a value; otherwise the
 * Rithmic figure as unavailable, keeping its reason. A HELM-calculated figure must say so: a
 * candidate claiming another source, the Rithmic label or a verification is refused.
 *
 * The figure not chosen rides along as `alternate` (details only, never mixed in). When both have
 * a value and they are more than [`DIFFERS_TOLERANCE_CENTS`] apart, the Rithmic figure carries a
 * visible "Differs from Rithmic by $X" note; when Rithmic has none, the HELM-calculated figure's
 * note says why Rithmic's is unavailable, and an incomplete HELM figure's reason is the note of the
 * unavailable Rithmic one.
 */
export function chooseRoom(rithmic: BrokerFigure, helmCalculated: BrokerFigure | null): BrokerFigure {
  const helmOk =
    helmCalculated !== null &&
    helmCalculated.kind === rithmic.kind &&
    helmCalculated.source === "helm_calculated" &&
    helmCalculated.verification === null &&
    helmCalculated.label === HELM_CALCULATED_LABEL &&
    helmCalculated.valueCents !== null;
  if (rithmic.source === "rithmic" && rithmic.valueCents !== null) {
    if (!helmOk || helmCalculated.valueCents === null) return rithmic;
    const diff = helmCalculated.valueCents - rithmic.valueCents;
    const note =
      absCents(diff) > DIFFERS_TOLERANCE_CENTS
        ? `Differs from Rithmic by ${dollarsText(diff)}: TradePrizm-calculated from your firm rules gives ${helmCalculated.valueCents < 0n ? "-" : ""}${dollarsText(helmCalculated.valueCents)}. Rithmic's figure is shown.`
        : "";
    return { ...rithmic, alternate: helmCalculated, note };
  }
  if (helmOk) {
    return {
      ...helmCalculated,
      alternate: rithmic,
      note: `Rithmic's figure is unavailable (${rithmic.reason}); this one is TradePrizm's own calculation from your firm rules.`,
    };
  }
  // A HELM figure that is not refused for its label but has no value (incomplete history, a
  // missing entry) explains itself next to the unavailable Rithmic figure.
  if (
    helmCalculated !== null &&
    helmCalculated.kind === rithmic.kind &&
    helmCalculated.verification === null &&
    helmCalculated.valueCents === null &&
    helmCalculated.source === "unavailable"
  ) {
    return {
      ...rithmic,
      alternate: helmCalculated,
      note: `TradePrizm-calculated from your firm rules: ${helmCalculated.reason}`,
    };
  }
  return rithmic;
}

/** The 356 threshold itself (a direct Rithmic value, no arithmetic). */
export function brokerThreshold(i: BrokerRiskInputs): {
  valueCents: bigint | null;
  reason: string;
  raw: string | null;
} {
  const reason = thresholdReason(i);
  return {
    valueCents: reason === "" ? (i.risk?.autoLiqThresholdCents ?? null) : null,
    reason,
    raw: i.risk && i.risk.autoLiqThresholdRaw !== "" ? i.risk.autoLiqThresholdRaw : null,
  };
}

// ---------------------------------------------------------------------------------------------
// Firm rules: not Rithmic data (owner 2026-10-02; owner entry per F2 as revised, ADR-0047).

export type FirmRuleKind = "profit_target" | "consistency" | "payout_window";

export type FirmRuleFigure = {
  kind: FirmRuleKind;
  label: string;
  /**
   * `owner_entry` ("Firm rule · your entry") for the owner's raw value; never `rithmic`.
   * Rooms computed from it (target-to-go, consistency, payout P&L) are `helm_calculated` in
   * tables/firmFigures.ts.
   */
  source: Exclude<FigureSource, "rithmic">;
  value: string | null;
  reason: string;
};

export const FIRM_RULE_LABELS: Readonly<Record<FirmRuleKind, string>> = {
  profit_target: "Profit target",
  consistency: "Consistency %",
  payout_window: "Payout window",
};

/** The owner's entry of a firm rule, already formatted for display (null = not entered). */
export type FirmRuleEntry = { value: string | null };

/**
 * A firm rule as shown: the owner's entry ("Firm rule · your entry") when there is one; otherwise
 * unavailable — not provided by Rithmic, and not entered.
 */
export function firmRule(kind: FirmRuleKind, entry?: FirmRuleEntry | null): FirmRuleFigure {
  if (entry?.value) {
    return { kind, label: FIRM_RULE_LABELS[kind], source: "owner_entry", value: entry.value, reason: "" };
  }
  return {
    kind,
    label: FIRM_RULE_LABELS[kind],
    source: "unavailable",
    value: null,
    reason:
      entry === undefined || entry === null
        ? "Not provided by Rithmic"
        : "Not provided by Rithmic; not entered",
  };
}

/** The label of HELM's own (legacy, automatic) limits wherever they are shown. */
export const HELM_LIMIT_LABEL = "TradePrizm limit · TradePrizm-calculated";
